import React, {FocusEventHandler, useEffect} from 'react';
import {Icon, Label, MaskedTextField, TextField, TooltipHost} from '@fluentui/react';
import MarkdownView from "react-showdown";
import {SettingId} from "../../settings/registry";

interface IDataFieldProps {
    required?: boolean,
    readOnly?: boolean,
    disabled?: boolean,
    label?: string,
    value?: string,
    type?: string,
    mask?: string,
    name?: string,
    maskFormat?: string,
    maskChar?: string,
    onChange?: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string, name?: string) => void,
    onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement | undefined>,
    hideIfEmpty?: boolean,
    placeholder?: string,
    multiline?: boolean,
    toolTip?: string,
    toolTipLocation?: 'left' | 'right';
    onKeyDown?: (e: React.KeyboardEvent) => void
    onGetErrorMessage?: (value: string) => string,
    rows?: number,
    labelActions?: React.ReactNode,
    maxLength?: number,
    dataTestId?: string,
    /** Registry identifier, so documentation can deep-link to this field. */
    settingId?: SettingId
}

const DataField: React.FC<IDataFieldProps> = (props) => {
    const [maskFormat, setMaskFormat] = React.useState<{ [key: string]: RegExp }>();
    useEffect(() => {
        if (props.maskFormat) {
            setMaskFormat({[props.maskChar ?? '*']: new RegExp(props.maskFormat)});
        }
    }, [props.maskFormat]);

    const getErrorMessage = (value: string): string => {
        if (props.onGetErrorMessage) {
            return props.onGetErrorMessage(value);
        }
        return value.length < 1 && props.required ? 'Field is required' : '';
    };
    // Only wraps when the field is registered, so uninstrumented call sites render exactly as before.
    const marked = (content: React.ReactElement) =>
        props.settingId ? <div data-setting-id={props.settingId}>{content}</div> : content;

    if (props.hideIfEmpty && (!props.value || props.value == '')) {
        return <></>
    }
    if (props.readOnly || props.disabled) {
        return (
            <div className={'ac-details-field'} data-setting-id={props.settingId}>
                {props.label && <h4>{props.label}</h4>}
                {props.value ?? props.placeholder}
            </div>
        )
    }
    const label = () => {
        if (props.toolTip) {
            return (<TooltipHost content={<MarkdownView markdown={props.toolTip}/>}>
                <div style={{display: "inline-flex"}}>
                    <Label required={props.required}>
                        {(props.toolTipLocation === 'left' || !props.toolTipLocation) && <Icon iconName={'Info'} style={{paddingRight: '5px'}}/>}
                        {props.label}
                        {props.labelActions}
                        {props.toolTipLocation === 'right' && <Icon iconName={'Info'} style={{paddingLeft: '5px'}}/>}
                    </Label>
                </div>
            </TooltipHost>)
        }
        return (
            <div style={{display: "inline-flex"}}>
                <Label required={props.required}>{props.label}</Label>
                {props.labelActions}
            </div>)
    }
    const handleChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (props.onChange) {
            props.onChange(event, newValue, props.name);
        }
    }
    if (props.mask) {
        return marked(
            <React.Fragment>
                {label()}
                <MaskedTextField
                    data-testid={props.dataTestId}
                    required={props.required}
                    type={props.type}
                    mask={props.mask}
                    maskFormat={maskFormat}
                    value={props.value ?? ""}
                    name={props.name}
                    onChange={handleChange}
                    onBlur={props.onBlur}
                    placeholder={props.placeholder}
                    styles={{root: {paddingBottom: '2px'}}}
                    onGetErrorMessage={getErrorMessage}
                    multiline={props.multiline}
                    rows={props.rows}
                    maxLength={props.maxLength}
                    onKeyDown={props.onKeyDown}
                />
            </React.Fragment>
        )
    }
    return marked(
        <React.Fragment>
            {label()}
            <TextField
                data-testid={props.dataTestId}
                draggable={false}
                type={props.type}
                required={props.required}
                value={props.value ?? ""}
                name={props.name}
                onChange={handleChange}
                onBlur={props.onBlur}
                placeholder={props.placeholder}
                styles={{root: {paddingBottom: '2px'}}}
                onGetErrorMessage={getErrorMessage}
                multiline={props.multiline}
                rows={props.rows}
                maxLength={props.maxLength}
                onKeyDown={props.onKeyDown}
            />
        </React.Fragment>
    );
}

export default DataField;